// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import { UilInfoCircle, UilQuestionCircle, UilImport, UilSync } from '@iconscout/react-unicons';

// Internal module imports
import MfrrClasses from '../index.module.css';
import { DropdownComponent } from '../../../components/Inputs/Input';
import classes from '../../../styles/AllDevices.module.css';
import { useLoader, useQueryParamWithDefault } from '../../../hooks';
import DatePicker from '../../../components/Inputs/DatePicker/DatePicker';
import { momentTimeFormater } from '../../../utils/timeHelper';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import Typography from '../../../components/Typography/Typography';
import Table from '../../../components/Table/Table';
import _ from 'lodash';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import MFRRActivationSignalViewer from './ModalComponent/MFRRActivationSignalViewer';
import { CamelCaseToTextCase } from '../../../utils/stringHelper';
import { DateParam } from 'use-query-params';
import { exportData } from '../../../utils/downloadHelper';
import IconComponent from '../../../components/Icon/Icon';
import { MFRRService } from '../../../services/MFRRService';

const LIMIT = 15;

const ActivationEvents = ({ cId, area }) => {
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('date', DateParam, new Date(), cId);
    const [data, setData] = useState([]);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [isAutoRefresh, setIsAutorefresh] = useState(false);
    const [seconds, setSeconds] = useState(30);
    const [counterInterval, setCounterInterval] = useState(null);
    const [signalViewerModal, setSignalViewerModal] = useState({ status: false, data: null });

    useEffect(() => {
        getActivationEvents();
    }, [date, skip]);

    const getActivationEvents = (isLoader = true, downloadData = false) => {
        const params = {
            limit: LIMIT,
            skip,
            area,
            date: moment(date).format('YYYY-MM-DD'),
        };
        const handler = !downloadData ? handleActivationEventsSuccess : handleDownload;

        MFRRService.GetActivationEvents(
            params,
            isLoader ? () => startLoader('getActivationEvents') : () => { },
            handler,
            handleError,
            isLoader ? () => stopLoader('getActivationEvents') : () => { }
        );
    };

    const handleActivationEventsSuccess = ({ data }) => {
        const tempData = data?.data?.data?.map((item) => ({
            customerName: item.customerName,
            edgeId: item.edgeId,
            edgeAlias: item.edgeAlias,
            instanceName: item.instanceName,
            askedAndMaxPower: `${_.get(item, 'maxAskedPower', '--')} / ${_.get(item, 'maxDeliveredPower', '--')}`,
            response: _.get(item, 'response', '--'),
            responseStatus: _.get(item, 'responseStatus', '--'),
            startTime: momentTimeFormater(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
            duration: moment(item.endTime).diff(item.startTime, 'minutes'),
            activationStatus: (
                <div className={`${MfrrClasses.ProfileIconContainer} ${item.status === 'Success' ? MfrrClasses.StatusSuccess : MfrrClasses.StatusUnderdelivered}`}>
                    {CamelCaseToTextCase(item.status)}
                </div>
            ),
            info: (
                <div onClick={() => setSignalViewerModal({ status: true, data: item })}>
                    <CustomTooltip content={'Signal Viewer'}>
                        <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                    </CustomTooltip>
                </div>
            ),
        }));
        setTotalCount(data?.data?.count || 0)
        setData(tempData);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    useEffect(() => {
        if (isAutoRefresh) {
            const interval = setInterval(() => {
                if (seconds > 0) {
                    setSeconds(seconds - 1);
                } else {
                    getActivationEvents(false);
                    setSeconds(30);
                }
            }, 1000);
            setCounterInterval(interval);
            return () => clearInterval(interval);
        }
    }, [isAutoRefresh, seconds]);

    const handleDownload = ({ data }) => {
        const processedData = data?.data?.data?.map((item) => ((item.duration = moment(item.endTime).diff(item.startTime, 'minutes')), item));
        exportData(processedData || [], `Activation Events -- ${area}`, [
            'customerName',
            'edgeId',
            'edgeAlias',
            'instanceName',
            'maxAskedPower',
            'maxDeliveredPower',
            'response',
            'responseStatus',
            'startTime',
            'endTime',
            'status',
            'duration',
        ]);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div>
            <div className={MfrrClasses.Filter}>
                <div className={MfrrClasses.LeftContainer} style={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
                    <div className={MfrrClasses.SelecterContainer}>
                        <label className={MfrrClasses.DatePickerLabel}>Date</label>
                        <DatePicker date={date} setDate={setDate} showArrow={true} />
                    </div>
                    <div onClick={() => getActivationEvents(true)}>
                        <IconComponent icon={UilSync} tooltip={'Refresh'} size="1.5vw" />
                    </div>
                    <div onClick={() => getActivationEvents(true, true)}>
                        <IconComponent icon={UilImport} tooltip={'Download'} size="1.5vw" />
                    </div>
                </div>
            </div>

            <div className={classes.AllDevices}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Activation Events" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={data.length || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <Table
                        head={[
                            'Customer Name',
                            'Edge ID',
                            'Edge Alias',
                            'Instance Name',
                            'Ask. Power / Max. Power',
                            'Act.Status',
                            <div>
                                <CustomTooltip content={'Response to energinet mentioning our system will activate or will be unavailable.'}>
                                    <div style={{ display: 'flex' }}>
                                        <div>Response</div>
                                        <UilQuestionCircle size="1vw" />
                                    </div>
                                </CustomTooltip>
                            </div>,
                            <div>
                                <CustomTooltip content={'Status of the response sent to energinet acknowledging the received command.'}>
                                    <div style={{ display: 'flex' }}>
                                        <div>Response Status</div>
                                        <UilQuestionCircle size="1vw" />
                                    </div>
                                </CustomTooltip>
                            </div>,
                            'Start Time (CET)',
                            'End Time (CET)',
                            'Duration (Min)',
                            'Info',
                        ]}
                        keys={[
                            'customerName',
                            'edgeId',
                            'edgeAlias',
                            'instanceName',
                            'askedAndMaxPower',
                            'activationStatus',
                            'response',
                            'responseStatus',
                            'startTime',
                            'endTime',
                            'duration',
                            'info',
                        ]}
                        data={data}
                        page={page}
                        Pagination={true}
                        limit={LIMIT}
                        handlePageChange={handlePageChange}
                        totalCount={totalCount}
                    />
                </div>
            </div>

            <ModalComponent
                isOpen={signalViewerModal.status}
                setOpen={(status) => {
                    setSignalViewerModal({ status: false, data: '' });
                }}
            >
                <MFRRActivationSignalViewer setOpen={setSignalViewerModal} data={signalViewerModal.data} />
            </ModalComponent>
        </div>
    );
};

export default ActivationEvents;
